import type { OsItensTypes } from '../../../../../../../services/Laboratorio'

/** Colunas da tabela que aceitam ordenação. */
export type ColunaOrdenavel =
  | 'data_coleta'
  | 'data_liberada'
  | 'propriedade_nome'
  | 'talhao_nome'
  | 'status'

export type DirecaoOrdenacao = 'asc' | 'desc'

export interface EstadoOrdenacao {
  coluna: ColunaOrdenavel
  direcao: DirecaoOrdenacao
}

/**
 * Compara dois valores de forma estável, empurrando vazios para o fim
 * independentemente da direção — uma OS sem data não deve disputar o topo da
 * lista quando o usuário ordena por data.
 */
function comparar(a: unknown, b: unknown, direcao: DirecaoOrdenacao): number {
  const vazioA = a === null || a === undefined || a === ''
  const vazioB = b === null || b === undefined || b === ''

  if (vazioA && vazioB) return 0
  if (vazioA) return 1
  if (vazioB) return -1

  let resultado: number

  if (typeof a === 'number' && typeof b === 'number') {
    resultado = a - b
  } else {
    // `numeric` faz "TH 2" vir antes de "TH 10"; sem isso a ordem seria alfabética.
    resultado = String(a).localeCompare(String(b), 'pt-BR', {
      numeric: true,
      sensitivity: 'base',
    })
  }

  return direcao === 'asc' ? resultado : -resultado
}

/**
 * Ordena a lista de OS por uma coluna.
 *
 * Ordenação client-side: a lista vem completa do servidor (`pagination = -1`),
 * então isto cobre todos os registros da propriedade, não um recorte.
 */
export function ordenarListaOs(
  lista: OsItensTypes[],
  ordenacao: EstadoOrdenacao | null
): OsItensTypes[] {
  if (!ordenacao) return lista ?? []

  const { coluna, direcao } = ordenacao

  // `slice` para não mutar a lista do react-query (o cache é compartilhado).
  return (lista ?? []).slice().sort((a: any, b: any) => comparar(a?.[coluna], b?.[coluna], direcao))
}

/**
 * Próximo estado ao clicar numa coluna: alterna asc/desc na mesma coluna, ou
 * começa em asc numa coluna nova.
 */
export function alternarOrdenacao(
  atual: EstadoOrdenacao | null,
  coluna: ColunaOrdenavel
): EstadoOrdenacao {
  if (atual?.coluna === coluna) {
    return { coluna, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
  }
  return { coluna, direcao: 'asc' }
}
